import { computed, onMounted } from '@vue/runtime-core';
import { useBrandStore } from '@/stores/brand';
import { storeToRefs } from 'pinia';

/**
 * Composable pour gérer les couleurs dynamiques par marque
 *
 * Utilisation:
 * const { brandClass, brandColor, brandHexColor } = useBrandColors();
 *
 * Ajouter la classe au root de votre composant:
 * <div :class="brandClass">...</div>
 */
export function useBrandColors() {
  const brandStore = useBrandStore();
  const { currentBrand } = storeToRefs(brandStore);

  // Récupérer la marque depuis le store (réactif)
  const getBrand = () => {
    return currentBrand.value || null;
  };

  // Classe CSS dynamique selon la marque
  const brandClass = computed(() => {
    const brand = currentBrand.value;
    if (!brand) return '';
    return `brand-${brand}`;
  });

  // Couleur de la marque en HSL (pour les variables CSS)
  const brandColor = computed(() => {
    const brand = currentBrand.value;
    if (!brand) return 'var(--primary)';

    const colors: Record<string, string> = {
      fendt: 'var(--fendt)',
      valtra: 'var(--valtra)',
      hv: 'var(--hv)',
    };
    return colors[brand] || 'var(--primary)';
  });

  // Couleur de la marque en HEX (pour usage dans le code)
  const brandHexColor = computed(() => {
    const brand = currentBrand.value;
    if (!brand) return '#B70018';

    const colors: Record<string, string> = {
      fendt: '#00A651',    // Vert Fendt UNIQUEMENT
      valtra: '#E2001A',   // Rouge Valtra
      hv: '#B70018',       // Rouge AGCO (Horizon Vert = AGCO)
    };
    return colors[brand] || '#B70018';
  });

  // Nom d'affichage de la marque
  const brandDisplayName = computed(() => {
    const brand = currentBrand.value;
    if (!brand) return 'AGCO';

    const names: Record<string, string> = {
      fendt: 'Fendt',
      valtra: 'Valtra',
      hv: 'Horizon Vert',
    };
    return names[brand] || 'AGCO';
  });

  // Appliquer la classe au body au montage
  const applyBrandClassToBody = () => {
    const brand = currentBrand.value;
    // Retirer toutes les classes de marque existantes
    document.body.classList.remove('brand-fendt', 'brand-valtra', 'brand-hv');
    // Ajouter la classe de la marque actuelle si elle existe
    if (brand) {
      document.body.classList.add(`brand-${brand}`);
    }
  };

  // Observer les changements du brand via l'événement custom
  const watchBrandChanges = () => {
    window.addEventListener('brand-changed', () => {
      applyBrandClassToBody();
    });
  };

  onMounted(() => {
    applyBrandClassToBody();
    watchBrandChanges();
  });

  return {
    brandClass,
    brandColor,
    brandHexColor,
    brandDisplayName,
    getBrand,
    applyBrandClassToBody,
  };
}

/**
 * Helper pour obtenir la couleur HEX d'une marque sans utiliser le composable
 */
export function getBrandHexColor(brand?: string): string {
  if (!brand) {
    const brandStore = useBrandStore();
    brand = brandStore.currentBrand || undefined;
  }

  if (!brand) return '#B70018';

  const colors: Record<string, string> = {
    fendt: '#00A651',    // Vert Fendt UNIQUEMENT
    valtra: '#E2001A',   // Rouge Valtra
    hv: '#B70018',       // Rouge AGCO (Horizon Vert = AGCO)
  };
  return colors[brand] || '#B70018';
}

/**
 * Helper pour obtenir le nom d'affichage d'une marque
 */
export function getBrandDisplayName(brand?: string): string {
  if (!brand) {
    const brandStore = useBrandStore();
    brand = brandStore.currentBrand || undefined;
  }

  if (!brand) return 'AGCO';

  const names: Record<string, string> = {
    fendt: 'Fendt',
    valtra: 'Valtra',
    hv: 'Horizon Vert',
  };
  return names[brand] || 'AGCO';
}
